import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideStar } from '@ng-icons/lucide';
import { TranslocoModule } from '@ngneat/transloco';
import { HttpErrorService, SvcAppSettings, isNullOrUndefined } from 'projects/lib-shared-common/src/public-api';
import { SvcBookmarkListById, SvcBookmarkListByUid } from 'projects/lib-shared-core/src/lib/interfaces/svc-bookmark.interface';
import { SvcBookmarkService } from 'projects/lib-shared-core/src/lib/services/svc-bookmark.service';
import { SitesService } from 'projects/lib-shared-core/src/public-api';
import { EMPTY, BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { ZardTooltipImports } from '@solvace/zard/shared/components/tooltip/tooltip.imports';
import { provideSolvaceIcons } from '../svz-icons/solvace-icons';
import { effectInputChanges } from '../utils/effect-input-changes';

@Component({
  selector: 'svz-bookmark-button',
  templateUrl: './svz-bookmark-button.component.html',
  styleUrl: './svz-bookmark-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideIcons({ lucideStar }), provideSolvaceIcons()],
  host: {
    '[class.cursor-pointer]': '!readonly() && !isLoading',
    '[class.svz-bookmark-interactive]': 'isDesignSystem() && !readonly() && !isLoading',
    '[class.opacity-50]': 'isLoading',
    '(click)': '$event.stopPropagation() || toggleValue()',
  },
  standalone: true,
  imports: [NgIcon, TranslocoModule, ...ZardTooltipImports],
})
export class SvzBookmarkButtonComponent implements OnInit, OnDestroy {
  public readonly referenceId = input.required<number | string>();
  public readonly applicationId = input<string>(undefined);
  public readonly siteId = input<number>(undefined);
  public readonly initialValue = input<boolean>(undefined);
  public readonly readonly = input(false);
  public readonly iconSizeClass = input<string>('icon-size-4');
  public readonly mockMode = input(false);
  public readonly dark = input(false);
  public readonly iconAlwaysFilled = input(false);

  /** Additive, defaults to current behavior (single `lucideStar` glyph, toggled via fill/text
   *  color classes). Set both to render distinct outline/filled icons instead — e.g. the design
   *  system's `svcStar`/`svcStarFill`, which mirror the prototype's own two-icon star exactly. */
  /** Additive, defaults to the existing rendering. `design-system` swaps the `text-yellow-500`/`#c4c8d4`
   *  treatment for the prototypes' own gold (`.fav`, `--color-gold` #e8b931) and its hover-on-outline
   *  state. Pair it with `iconOutline`/`iconFilled` set to `svcStar`/`svcStarFill`. */
  public readonly variant = input<'default' | 'design-system'>('default');

  /** Additive. Forwarded to `ng-icon`'s own `size`; undefined leaves sizing to `iconSizeClass`. */
  public readonly iconSize = input<string>(undefined);

  /** Additive. Transloco keys for the tooltip and aria-label; the defaults keep the current wording. */
  public readonly addLabel = input('Favoritar');
  public readonly removeLabel = input('Desfavoritar');

  protected readonly isDesignSystem = computed(() => this.variant() === 'design-system');

  public readonly iconOutline = input('lucideStar');
  public readonly iconFilled = input('lucideStar');

  public readonly onValueChanged = output<boolean>();
  public readonly onToggleError = output<boolean>();
  public readonly onInitialValueObtained = output<boolean>();

  #httpErrorService = inject(HttpErrorService);
  #sitesService = inject(SitesService);
  #bookmarkService = inject(SvcBookmarkService);
  #appSettings = inject(SvcAppSettings);
  #cdr = inject(ChangeDetectorRef);

  #wasInitialized = false;
  #wasDistroyed = false;
  #requestSubscription: Subscription;
  #destroy$ = new Subject<void>();

  protected value: boolean;
  protected isLoading$ = new BehaviorSubject(false);

  protected get isLoading(): boolean {
    return this.isLoading$.value;
  }

  protected set isLoading(value: boolean) {
    this.isLoading$.next(value);
    // `value`/`isLoading` are plain fields mutated from inside async RxJS callbacks, not signals —
    // on this OnPush component nothing else marks it dirty, so the icon and host classes silently
    // kept showing the pre-click state after a real (non-mock) toggle. Same fix as the sibling
    // `svz-totals-status`, which already calls `detectChanges()` after each async mutation.
    this.#cdr.detectChanges();
  }

  public ngOnInit(): void {
    if (isNullOrUndefined(this.initialValue())) {
      this._get();
    }
    this.#wasInitialized = true;
  }

  private readonly syncInputChanges = effectInputChanges(
    {
      referenceId: () => this.referenceId(),
      applicationId: () => this.applicationId(),
      siteId: () => this.siteId(),
      initialValue: () => this.initialValue(),
      readonly: () => this.readonly(),
      iconSizeClass: () => this.iconSizeClass(),
      mockMode: () => this.mockMode(),
      dark: () => this.dark(),
      iconAlwaysFilled: () => this.iconAlwaysFilled(),
    },
    (changes) => {
      if (this.#wasInitialized && ('referenceId' in changes || 'applicationId' in changes || 'siteId' in changes)) {
        this._get();
      }
      if ('initialValue' in changes) {
        this.value = this.initialValue();
      }
    }
  );

  public refresh(): void {
    this._get();
  }

  private _get(): void {
    const referenceId = this.referenceId();
    if (!this.mockMode() && referenceId && !this.#wasDistroyed) {
      this.#requestSubscription?.unsubscribe();
      this.isLoading = true;
      const appId = this.applicationId() ?? this.#appSettings.applicationId;
      const siteId = this.siteId() ?? this.#sitesService.currentSite.siteId;
      const request: Observable<(SvcBookmarkListByUid | SvcBookmarkListById)[]> =
        typeof referenceId === 'string'
          ? this.#bookmarkService.getBookmarkListByUIds(appId, siteId, [referenceId])
          : this.#bookmarkService.getBookmarkListByIds(appId, siteId, [referenceId]);
      this.#requestSubscription = request
        .pipe(
          takeUntil(this.#destroy$),
          tap((response) => {
            this.value = response.some((item) => (item['registryUniqueId'] || item['registryUniqueUId']) === this.referenceId());
            this.onInitialValueObtained.emit(this.value);
          }),
          finalize(() => {
            this.#requestSubscription = null;
            this.isLoading = false;
          })
        )
        .subscribe();
    }
  }

  public toggleValue(): void {
    if (this.readonly() || this.isLoading || this.#wasDistroyed) {
      return;
    }

    const currentValue = this.value ?? false;
    const appId = this.applicationId() ?? this.#appSettings.applicationId;
    const siteId = this.siteId() ?? this.#sitesService.currentSite.siteId;

    const onSuccess = () => {
      this.value = !currentValue;
      this.onValueChanged.emit(this.value);
    };

    const onError = (error: any) => {
      if ('UQ_RegistryBookmark' in (error.error?.errors ?? {})) {
        this.value = !currentValue;
        this.onValueChanged.emit(this.value);
      } else {
        this.onToggleError.emit(!currentValue);
        this.#httpErrorService.showErrorInToast(error);
      }
      return EMPTY;
    };

    const onFinalize = () => {
      this.#requestSubscription = null;
      this.isLoading = false;
    };

    const buildRequest = (): Observable<unknown> => {
      if (currentValue) {
        const referenceId = this.referenceId();
        return typeof referenceId === 'string'
          ? this.#bookmarkService.deleteBookmarkRegistryUid(referenceId, appId, siteId)
          : this.#bookmarkService.deleteBookmarkRegistryId(referenceId, appId, siteId);
      }
      const referenceId = this.referenceId();
      return typeof referenceId === 'string'
        ? this.#bookmarkService.addBookmarkRegistryUid(referenceId, appId, siteId)
        : this.#bookmarkService.addBookmarkRegistryId(referenceId, appId, siteId);
    };

    this.#requestSubscription?.unsubscribe();
    this.isLoading = true;
    buildRequest()
      .pipe(
        takeUntil(this.#destroy$),
        tap(() => onSuccess()),
        catchError((error) => onError(error)),
        finalize(() => onFinalize())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.#wasDistroyed = true;
    this.#destroy$?.next();
    this.#destroy$?.complete();
    this.#requestSubscription?.unsubscribe();
  }
}
